import {type DefaultError, useMutation, useQueryClient} from "@tanstack/react-query";
import {queryHttp} from "~/query/queryHttp";
import {notificationStore} from "~/stores/init";
import {supplierEntityMoveMemberUrl} from "~/pages/supplier-360/services/supplier360Urls";
import type {SupplierMoveMemberResult} from "~/pages/supplier-360/types/Supplier360Types";

export type MoveSupplierClusterMemberVariables = {
    /** The supplier the row moves into. */
    entityId: number;
    /** The row's `ClusterMember.id`. */
    memberId: number;
    /** Notification copy only — never sent. */
    displayName: string;
};

/**
 * `POST supplier_360/entity/{id}/move_member/` — moves a raw row, with its spend, into this supplier's
 * cluster. A supplier left without raw rows stays as it is.
 *
 * Both suppliers' spend, cluster and header change, so every Supplier 360 query is refreshed: moves are
 * rare enough for that to cost nothing noticeable.
 */
export function useMoveSupplierClusterMemberMutation() {
    const queryClient = useQueryClient();

    return useMutation<SupplierMoveMemberResult, DefaultError, MoveSupplierClusterMemberVariables>({
        mutationFn: async ({entityId, memberId}) => {
            const {data} = await queryHttp.post<SupplierMoveMemberResult>(
                supplierEntityMoveMemberUrl(entityId),
                {member_id: memberId},
            );
            return data;
        },
        onSuccess: (result, {entityId, displayName}) => {
            void queryClient.invalidateQueries({
                predicate: (query) => {
                    const key = query.queryKey[0];
                    return typeof key === 'string' && key.startsWith('supplier_360/');
                },
            });
            notificationStore.show(`${displayName} moved to ${result.canonical_name}.`, {
                severity: 'success',
                id: `cluster-member-move-success-${entityId}`,
                autoHideDuration: 3000,
            });
        },
        onError: (_error, {entityId}) => {
            notificationStore.show('Could not move the raw row. Please try again.', {
                severity: 'error',
                id: `cluster-member-move-error-${entityId}`,
                autoHideDuration: 4000,
            });
        },
    });
}
